import { useAppDispatch } from '@hooks/reduxHooks'
import { useSelector } from '@state/store'
import { Box, Button, Dialog, FormControl, IconButton, InputLabel, MenuItem, Select, SelectChangeEvent, TextField, Tooltip, Typography } from '@mui/material'
import { GridToolbarContainer, GridToolbarExport } from '@mui/x-data-grid-pro'
import { useState } from 'react'
import moment from 'moment'
import { WasteContainerDto } from '@services/dtos'
import { WasteEntrySource } from '@services/wasteEntryService'
import { getWasteEntries, setWasteEntryFilters } from '@state/reducers/wasteEntryReducer'
import { WasteEntrySourceOptions } from '@utils/constants'
import ServerAutocomplete from '@components/autocomplete/ServerAutocomplete'
import { wasteContainerService } from '@services/wasteContainerService'
import WasteContainerChips from '@components/WasteContainerChips'
import WasteEntryForm from '@components/forms/WasteEntryForm'
import { UpTransition } from '@components/animations'
import { WasteTableTabs } from '../WasteTableTabs'
import { DateInputField } from '@components/inputs/DateInputField'
import { Sync } from '@mui/icons-material'

const WasteEntriesTableExport = () => (
  <GridToolbarExport
    csvOptions={{
      fileName: 'waste-entries',
      delimiter: ';',
      utf8WithBom: true,
    }}
  />
)

export const WasteEntriesTableExportToolbar = () => {
  return (
    <GridToolbarContainer>
      <WasteEntriesTableExport />
    </GridToolbarContainer>
  )
}

const WasteEntriesTableToolbar = () => {
  const dispatch = useAppDispatch()
  const filters = useSelector(({ wasteEntry }) => wasteEntry.filters)
  const [selectedContainer, setSelectedContainer] = useState<WasteContainerDto | null>(null)
  const [formOpen, setFormOpen] = useState(false)

  const fetchContainers = async (search: string) => {
    const trimmed = search.trim()
    const no = /^\d+$/.test(trimmed) ? Number(trimmed) : undefined
    const page = await wasteContainerService.getAll({
      search: no ? undefined : trimmed || undefined,
      no,
      limit: 20,
      page: 0,
    })
    return page?.datas ?? []
  }

  const applyFilter = (patch: Parameters<typeof setWasteEntryFilters>[0]) => {
    dispatch(setWasteEntryFilters({ ...patch, page: 0 }))
    dispatch(getWasteEntries())
  }

  return (
    <GridToolbarContainer className="m-2 mb-4">
      <Box className="flex items-center gap-2 flex-wrap">
        <WasteTableTabs />

        <Button
          variant="contained"
          color="primary"
          size="small"
          className="!p-[7px]"
          onClick={() => setFormOpen(true)}
        >
          Nouveau déchet
        </Button>

        <Tooltip title="Actualiser" arrow>
          <IconButton onClick={() => dispatch(getWasteEntries())}>
            <Sync />
          </IconButton>
        </Tooltip>

        <ServerAutocomplete<WasteContainerDto>
          autoFetchOnMount
          value={selectedContainer}
          width={280}
          textFieldProps={{ size: 'small' }}
          fetchService={fetchContainers}
          onChange={(container) => {
            setSelectedContainer(container)
            applyFilter({ containerId: container?.id ?? undefined })
          }}
          getValue={(c) => c?.id ?? null}
          getOptionLabel={(value) => {
            const c = value as WasteContainerDto
            return c ? `#${c.no} ${c.label}` : ''
          }}
          renderOption={(props, c) => (
            <li {...props} key={c.id}>
              <Box sx={{ py: 0.25, width: '100%' }}>
                <Typography variant="body2">{`#${c.no} ${c.label}`}</Typography>
                <WasteContainerChips showStatus container={c} />
              </Box>
            </li>
          )}
          placeholder="Filtrer par N°, label ou code..."
          label="Conteneur à déchets"
          minSearchLength={0}
        />

        <FormControl size="small">
          <InputLabel>Source</InputLabel>
          <Select
            value={filters?.source ?? ''}
            onChange={(e: SelectChangeEvent) => {
              const value = e.target.value as WasteEntrySource | ''
              applyFilter({ source: value || undefined, sourceNo: undefined })
            }}
            label="Source"
            sx={{ minWidth: 150 }}
          >
            <MenuItem value=""><em>Toutes</em></MenuItem>
            {WasteEntrySourceOptions.map(({ value, label }) => (
              <MenuItem key={value} value={value}>{label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          size="small"
          label="N° déchet"
          type="number"
          value={filters?.no ?? ''}
          onChange={(e) => applyFilter({ no: e.target.value ? Number(e.target.value) : undefined })}
          sx={{ width: 120 }}
          inputProps={{ min: 1 }}
        />

        {(filters?.source === 'PREPARATION' || filters?.source === 'ELUTION') && (
          <TextField
            size="small"
            label={filters.source === 'PREPARATION' ? 'N° préparation' : 'N° élution'}
            type="number"
            value={filters?.sourceNo ?? ''}
            onChange={(e) => applyFilter({ sourceNo: e.target.value ? Number(e.target.value) : undefined })}
            sx={{ width: 150 }}
            inputProps={{ min: 1 }}
          />
        )}

        <DateInputField
          label="Du"
          value={filters?.from ? moment(filters.from) : null}
          onChange={(date) => applyFilter({ from: date ? date.startOf('day').toISOString() : undefined })}
        />
        <Typography variant="body2" sx={{ alignSelf: 'center' }}>au</Typography>
        <DateInputField
          label="Au"
          value={filters?.to ? moment(filters.to) : null}
          onChange={(date) => applyFilter({ to: date ? date.endOf('day').toISOString() : undefined })}
        />

        <WasteEntriesTableExport />
      </Box>

      <Dialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        fullWidth
        maxWidth="sm"
        TransitionComponent={UpTransition}
      >
        <WasteEntryForm onClose={() => setFormOpen(false)} />
      </Dialog>
    </GridToolbarContainer>
  )
}

export default WasteEntriesTableToolbar
